import { EVERYONE_SENTINEL, RegionId, UserId } from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { Content, PageHeader, Stage } from "components/layout";
import { Col, Row } from "components/shared/grid";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { CrmTablePagination } from "components/table/pagination";
import { Select } from "components/ui/select";
import {
    AdvancedSearchEntityType,
    useGetEntitiesWithoutActiveTasksPaginatedQuery,
    useGetUsersForRegionQuery,
} from "generated/graphql";
import { useTaskNouns } from "hooks/use-task-nouns";
import * as React from "react";
import {
    useCanSeeReferralSourceOwners,
    useCurrentUserId,
    useRegionId,
} from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { useViewableTaskTypes } from "../../../constants/tasks";
import { EntityListItem } from "./entity-list-item";

const ENTITY_TYPE_OPTIONS = [
    { value: AdvancedSearchEntityType.Client, label: "Active Clients" },
    { value: AdvancedSearchEntityType.Community, label: "Communities" },
    { value: AdvancedSearchEntityType.ReferenceContact, label: "Referral Contacts" },
    {
        value: AdvancedSearchEntityType.ReferenceBusiness,
        label: "Referral Organizations",
    },
    {
        value: AdvancedSearchEntityType.HomeCareLead,
        label: "Home Care Clients",
    },
];

const getEntityDisplayName = (entityType: AdvancedSearchEntityType, count: number) => {
    switch (entityType) {
        case AdvancedSearchEntityType.Client:
            return count === 1 ? "client" : "clients";
        case AdvancedSearchEntityType.Community:
            return count === 1 ? "community" : "communities";
        case AdvancedSearchEntityType.ReferenceContact:
            return count === 1 ? "referral contact" : "referral contacts";
        case AdvancedSearchEntityType.ReferenceBusiness:
            return count === 1 ? "referral organization" : "referral organizations";
        case AdvancedSearchEntityType.HomeCareLead:
            return count === 1 ? "home care client" : "home care clients";
        default:
            return count === 1 ? "record" : "records";
    }
};

const shouldShowAssignedToFilterForEntityType = (
    entityType: AdvancedSearchEntityType,
    canSeeReferralSourceOwners: boolean,
) => {
    switch (entityType) {
        case AdvancedSearchEntityType.Client:
            return true;
        case AdvancedSearchEntityType.Community:
            return false;
        case AdvancedSearchEntityType.ReferenceContact:
        case AdvancedSearchEntityType.ReferenceBusiness:
            return canSeeReferralSourceOwners;
        case AdvancedSearchEntityType.HomeCareLead:
            return true;
        default:
            return false;
    }
};

export const EntitiesWithoutActiveTasks: React.FC = () => {
    const nouns = useTaskNouns();
    const regionId = useRegionId();
    const canSeeReferralSourceOwners = useCanSeeReferralSourceOwners();
    const currentUserId = useCurrentUserId();
    const viewableTaskTypes = useViewableTaskTypes();
    const entityTypeOptions = React.useMemo(() => {
        if (viewableTaskTypes.length === 0) {
            return [];
        }
        const supportedKinds = new Set(viewableTaskTypes.map(x => x.kind));
        return ENTITY_TYPE_OPTIONS.filter(option => supportedKinds.has(option.value));
    }, [viewableTaskTypes]);

    const [selectedEntityType, setSelectedEntityType] =
        React.useState<AdvancedSearchEntityType>(AdvancedSearchEntityType.Client);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(25);
    const [assignedUserId, setAssignedUserId] = React.useState<SelectableUser>(
        currentUserId || EVERYONE_SENTINEL,
    );

    const shouldShowAssignedToFilter = React.useMemo(() => {
        return shouldShowAssignedToFilterForEntityType(
            selectedEntityType,
            canSeeReferralSourceOwners,
        );
    }, [selectedEntityType, canSeeReferralSourceOwners]);

    const finalAssignedUserId = React.useMemo(() => {
        if (!shouldShowAssignedToFilter) {
            return undefined;
        }
        if (assignedUserId === EVERYONE_SENTINEL) {
            return undefined;
        }
        if (assignedUserId === "") {
            return "";
        }
        return assignedUserId as UserId;
    }, [shouldShowAssignedToFilter, assignedUserId]);

    const queryParams = React.useMemo(
        () => ({
            regionId: regionId as RegionId,
            entityType: selectedEntityType,
            page,
            pageSize,
            assignedUserId: finalAssignedUserId,
        }),
        [regionId, selectedEntityType, page, pageSize, finalAssignedUserId],
    );

    const entitiesQuery = useGetEntitiesWithoutActiveTasksPaginatedQuery(queryParams, {
        keepPreviousData: true,
        enabled: !!regionId,
    });

    const getUsers = useGetUsersForRegionQuery(
        { regionId: regionId as RegionId },
        { ...stableQueryOptions(), enabled: !!regionId },
    );

    const handleTaskCreated = React.useCallback(async () => {
        await entitiesQuery.refetch();
    }, [entitiesQuery]);

    const handleEntityTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value as AdvancedSearchEntityType;
        setSelectedEntityType(value);
        setPage(1);
    };

    const handleAssignedUserChange = (userId: SelectableUser) => {
        setAssignedUserId(userId);
        setPage(1);
    };

    const entities = React.useMemo(() => {
        return entitiesQuery.data?.getEntitiesWithoutActiveTasksPaginated?.entities || [];
    }, [entitiesQuery.data]);

    const paginationData = entitiesQuery.data?.getEntitiesWithoutActiveTasksPaginated;

    const entityCounts = React.useMemo(() => {
        if (!paginationData?.entities) {
            return { total: 0, byType: {} };
        }

        const entities = paginationData.entities;
        const byType: Record<string, number> = {};

        entities.forEach(entity => {
            byType[entity.kind] = (byType[entity.kind] || 0) + 1;
        });

        return {
            total: paginationData.totalCount,
            byType,
        };
    }, [paginationData]);

    return (
        <Row type="flex">
            <Col className="component-wrapper">
                <Stage>
                    <PageHeader title={nouns.titlePlural} />
                    <Content>
                        <div className="space-y-1 sm:space-y-2 md:space-y-0 md:flex md:space-x-2 md:items-center">
                            <div className="flex-1">
                                <Select
                                    label="Record Type"
                                    disabled={entityTypeOptions.length === 0}
                                    onChange={handleEntityTypeChange}
                                    value={selectedEntityType}>
                                    {entityTypeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                            {shouldShowAssignedToFilter && (
                                <div className="flex-1">
                                    <UserSelect
                                        label="Assigned To"
                                        value={assignedUserId}
                                        onChange={handleAssignedUserChange}
                                        includeEveryone={true}
                                        includeUnassigned={true}
                                        everyoneLabel="All Users"
                                        unassignedLabel="Unassigned"
                                    />
                                </div>
                            )}
                        </div>

                        {paginationData && (
                            <div className="mt-4 text-sm text-gray-600">
                                <p>
                                    Found{" "}
                                    {entityCounts.total === 1
                                        ? "one"
                                        : entityCounts.total}{" "}
                                    {getEntityDisplayName(
                                        selectedEntityType,
                                        entityCounts.total,
                                    )}{" "}
                                    with{" "}
                                    {entityCounts.total === 1
                                        ? "no next step"
                                        : "no next steps"}
                                </p>
                            </div>
                        )}
                    </Content>

                    <div className="mt-2 lg:mt-4">
                        <Content>
                            <QueryRenderer
                                name="EntitiesWithoutActiveTasks"
                                query={entitiesQuery}>
                                {() => (
                                    <div>
                                        {entities.length === 0 ? (
                                            <div className="text-center py-8 text-gray-500">
                                                <p>
                                                    All{" "}
                                                    {getEntityDisplayName(
                                                        selectedEntityType,
                                                        2,
                                                    )}{" "}
                                                    have next steps.
                                                </p>
                                            </div>
                                        ) : (
                                            <ul className="divide-y divide-gray-200">
                                                {entities.map(entity => (
                                                    <li key={entity.id}>
                                                        <EntityListItem
                                                            entity={entity}
                                                            users={
                                                                getUsers.data
                                                                    ?.getUsersForRegion ||
                                                                []
                                                            }
                                                            onTaskCreated={
                                                                handleTaskCreated
                                                            }
                                                        />
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                            </QueryRenderer>

                            {paginationData && paginationData.totalPages > 1 && (
                                <div className="mt-6">
                                    <CrmTablePagination
                                        pageIndex={page - 1}
                                        canPreviousPage={page > 1}
                                        canNextPage={page < paginationData.totalPages}
                                        pageCount={paginationData.totalPages}
                                        nextPage={() =>
                                            setPage(prev =>
                                                Math.min(
                                                    prev + 1,
                                                    paginationData.totalPages,
                                                ),
                                            )
                                        }
                                        previousPage={() =>
                                            setPage(prev => Math.max(prev - 1, 1))
                                        }
                                        gotoPage={(pageIndex: number) =>
                                            setPage(pageIndex + 1)
                                        }
                                        pageSize={pageSize}
                                        setPageSize={setPageSize}
                                        count={paginationData.totalCount}
                                        allowAll={false}
                                    />
                                </div>
                            )}
                        </Content>
                    </div>
                </Stage>
            </Col>
        </Row>
    );
};
